import {
  Avatar,
  Box,
  Button,
  Chip,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import { Fragment, useMemo } from "react";
import { pascalToTitleCase } from "@features/orders-features/functions/helpers";
import { createSavedCurrencyFormatter } from "shared-ui/src/functions/currency";
import {
  CombinedWarehouseOrderItemDto,
  FulfillmentStatus,
  OrderLineItemDto,
  OrderLineRelatedDataDto,
  OrderLineSerialDto,
} from "@lib/ShiOneClient";
import { getRepresentativeLine, type ProductRow } from "./ProductTable";
import NoProductImage from "@images/no-product-image-available.svg";
import { toFulfillmentStatus } from "@features/orders-features/functions/getFulfillmentStatus";
import { getOrderStatusChip } from "@features/orders-features/components/orderStatusChips";
import { useOrderTelemetrySection } from "../../orderTelemetrySection";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import {
  AttachMoneyOutlined,
  Email,
  VolunteerActivismOutlined,
} from "@mui/icons-material";
import ProductSerialNumbers from "@features/orders-features/components/order-drawer/sections/content/ProductSerialNumbers";
import ProductCustomFields from "@features/orders-features/components/order-drawer/sections/content/ProductCustomFields";
import { ProductPickTickets } from "./ProductPickTickets";
import shiPalette from "shared-ui/src/theme/shiPalette";
import { ProductInventoryStatus } from "@features/orders-features/components/order-drawer/sections/content/ProductInventoryStatus";
import { BuyAgainButton } from "@features/orders-features/components/BuyAgainButton";
import { LineItem } from "@features/orders-features/types/drawer";

type ProductTableRowProps = {
  row: ProductRow;
  currencyCode?: string | null;
  formatPrice?: (amount: number) => string;
  onProductClick?: (item: OrderLineItemDto) => void;
  canOpenProduct?: (item: OrderLineItemDto) => boolean;
  getSerials: (
    lineNumber: number | undefined,
  ) => OrderLineSerialDto[] | undefined;
  getRelatedData: (
    lineNumber: number | undefined,
  ) => OrderLineRelatedDataDto[] | undefined;
  getWarehouseOrderLineItems: (
    lineNumber: number | undefined,
  ) => CombinedWarehouseOrderItemDto[] | undefined;
  isBackorder: (
    productDescriptor: string | undefined,
    productClass: string | undefined,
  ) => boolean;
  showStatusColumn?: boolean;
  showActionsColumn?: boolean;
};

const PRODUCT_IMAGE_WIDTH = 71.25;
const PRODUCT_IMAGE_HEIGHT = 60;
const PRODUCT_IMAGE_TEXT_GAP = 2;

const STATUS_CHIP_DISPLAY_ORDER = [
  "Processing",
  "Shipped",
  "Delivered",
] as const;

const sortStatusEntries = (byStatus: Record<string, number>) => {
  const orderByStatus = new Map<string, number>(
    STATUS_CHIP_DISPLAY_ORDER.map((label, index) => [
      label.toLowerCase(),
      index,
    ]),
  );

  return Object.entries(byStatus).sort(([leftStatus], [rightStatus]) => {
    const leftOrder = orderByStatus.get(leftStatus.toLowerCase());
    const rightOrder = orderByStatus.get(rightStatus.toLowerCase());

    if (leftOrder != null && rightOrder != null) {
      return leftOrder - rightOrder;
    }
    if (leftOrder != null) {
      return -1;
    }
    if (rightOrder != null) {
      return 1;
    }

    return leftStatus.localeCompare(rightStatus);
  });
};

const renderStatusChips = (
  line: LineItem,
  byStatus: Record<string, number>,
) => {
  const totalStatus = Object.keys(byStatus).length;

  return sortStatusEntries(byStatus)
    .map(([status, qty]) => {
      const fulfillment = toFulfillmentStatus(status);
      const chip = getOrderStatusChip(fulfillment);
      let { color, icon, iconColor } = chip;
      const { borderColor } = chip;
      if (fulfillment === FulfillmentStatus.NotApplicable) {
        if (status.startsWith("Service")) {
          color = shiPalette.success["50p"];
          iconColor = shiPalette.success.light;
          icon = (
            <VolunteerActivismOutlined fontSize="small" sx={{ pl: 0.5 }} />
          );
        } else if (status === "Fees") {
          color = shiPalette.success["50p"];
          iconColor = shiPalette.success.light;
          icon = <AttachMoneyOutlined fontSize="small" sx={{ pl: 0.5 }} />;
        } else if (status === "Email Delivery") {
          color = shiPalette.success["50p"];
          iconColor = shiPalette.success.light;
          icon = <Email fontSize="small" sx={{ pl: 0.5 }} />;
        } else {
          return null;
        }
      }

      const pStatus = pascalToTitleCase(status);
      const qtyTotal = line.item.quantity ?? 0;

      let label = pStatus;
      if (totalStatus > 1) {
        if (qty > 0 && qtyTotal > 0) {
          label = `${pStatus} (${qty} of ${qtyTotal})`;
        } else if (qty > 0) {
          label = `${pStatus} (${qty})`;
        }
      }

      return (
        <Chip
          key={status}
          icon={icon}
          label={label}
          size="small"
          variant="outlined"
          sx={{
            borderColor: borderColor ?? color,
            color,
            "& .MuiChip-icon": { color: iconColor ?? color },
            width: "fit-content",
          }}
        />
      );
    })
    .filter(Boolean);
};

export function ProductTableRow({
  row,
  currencyCode,
  formatPrice,
  onProductClick,
  canOpenProduct,
  getSerials,
  getRelatedData,
  getWarehouseOrderLineItems,
  isBackorder,
  showStatusColumn = true,
  showActionsColumn = true,
}: Readonly<ProductTableRowProps>) {
  const telemetrySection = useOrderTelemetrySection();
  const formatter = useMemo(() => createSavedCurrencyFormatter(currencyCode), [currencyCode]);
  const format = formatPrice ?? formatter.format;
  const line = getRepresentativeLine(row.lines);
  const productItem = line.item as unknown as OrderLineItemDto;
  const canOpen =
    onProductClick != null && (canOpenProduct?.(productItem) ?? true);

  const total =
    line.item.extendedPrice ??
    (line.item.unitPrice ?? 0) * (line.item.quantity ?? 0);

  const serials = getSerials(line.item.lineNumber);
  const relatedData = getRelatedData(line.item.lineNumber);
  const warehouseOrderLineItems = getWarehouseOrderLineItems(
    line.item.lineNumber,
  );
  const backorder = isBackorder(
    line.item.productDescriptor,
    line.item.productClass,
  );

  const productName =
    line.item.productName || line.item.productDescription || "Unknown Product";

  const orderLineItemDtoList = useMemo(
    () => [new OrderLineItemDto(line.item)],
    [line.item],
  );

  return (
    <Fragment>
      <TableRow
        sx={{
          "& > td": {
            borderBottom: 0,
          },
        }}
      >
        <TableCell sx={{ py: 1.5, px: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `${PRODUCT_IMAGE_WIDTH}px minmax(0, 1fr)`,
              columnGap: PRODUCT_IMAGE_TEXT_GAP,
              minWidth: 0,
              alignItems: "center",
            }}
          >
            <Avatar
              variant="rounded"
              src={line.item.imageUrl ?? NoProductImage}
              alt={
                line.item.productName ??
                line.item.mfgPartNumber ??
                "Unknown Product"
              }
              sx={{
                width: PRODUCT_IMAGE_WIDTH,
                height: PRODUCT_IMAGE_HEIGHT,
                bgcolor: "transparent",
                overflow: "hidden",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "& .MuiAvatar-img": {
                  objectFit: "contain",
                  objectPosition: "center",
                },
              }}
            />

            <Box sx={{ minWidth: 0 }}>
              <Button
                disabled={!canOpen}
                onClick={() => onProductClick?.(productItem)}
                size="small"
                variant="text"
                color="primary"
                sx={{
                  p: 0,
                  minWidth: 0,
                  textTransform: "none",
                  justifyContent: "flex-start",
                  width: "100%",
                }}
                {...telemetryProps({
                  name: "orders_product_open",
                  section: telemetrySection,
                  label: "Open product details",
                  ...((line.item.productDescriptor ?? line.item.mfgPartNumber)
                    ? {
                        id: String(
                          line.item.productDescriptor ??
                            line.item.mfgPartNumber,
                        ),
                      }
                    : {}),
                })}
              >
                <Tooltip title={productName}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "primary.main",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      cursor: canOpen ? "pointer" : "default",
                      "&:hover": canOpen
                        ? { textDecoration: "underline" }
                        : undefined,
                    }}
                  >
                    {productName}
                  </Typography>
                </Tooltip>
              </Button>
              <Typography variant="caption" color="text.secondary">
                Mfr #: {line.item.mfgPartNumber || "—"} SHI #:{" "}
                {line.item.shiPartNumber || line.item.productDescriptor || "—"}
              </Typography>
            </Box>
          </Box>
        </TableCell>

        {showStatusColumn && (
          <TableCell sx={{ py: 1.5, px: 2 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {renderStatusChips(line, row.byStatus)}
            </Box>
          </TableCell>
        )}

        <TableCell align="right" sx={{ py: 1.5, px: 2 }}>
          <Typography variant="body2" color="textPrimary">
            {format(line.item.unitPrice ?? 0)}
          </Typography>
        </TableCell>

        <TableCell align="right" sx={{ py: 1.5, px: 2 }}>
          <Typography variant="body2" color="textPrimary">
            {line.item.quantity ?? 0}
          </Typography>
        </TableCell>

        <TableCell align="right" sx={{ py: 1.5, px: 2 }}>
          <Typography variant="body2" color="textPrimary">
            {format(total)}
          </Typography>
        </TableCell>

        {showActionsColumn && (
          <TableCell sx={{ py: 1.5, px: 2 }}>
            <BuyAgainButton items={orderLineItemDtoList} />
          </TableCell>
        )}
      </TableRow>

      <ProductSerialNumbers
        xWidth={PRODUCT_IMAGE_WIDTH}
        xGap={PRODUCT_IMAGE_TEXT_GAP}
        lineNumber={line.item.lineNumber ?? undefined}
        serials={serials ?? undefined}
      />

      <ProductCustomFields
        xWidth={PRODUCT_IMAGE_WIDTH}
        xGap={PRODUCT_IMAGE_TEXT_GAP}
        lineNumber={line.item.lineNumber ?? undefined}
        relatedData={relatedData ?? undefined}
      />

      <ProductPickTickets
        xWidth={PRODUCT_IMAGE_WIDTH}
        xGap={PRODUCT_IMAGE_TEXT_GAP}
        lineNumber={line.item.lineNumber ?? undefined}
        warehouseOrderLineItems={warehouseOrderLineItems ?? undefined}
      />

      {backorder && (
        <ProductInventoryStatus
          xWidth={PRODUCT_IMAGE_WIDTH}
          xGap={PRODUCT_IMAGE_TEXT_GAP}
        />
      )}
    </Fragment>
  );
}
